'use client'

import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import Link from 'next/link'
import Footer from '@/components/Footer'
import Navbar from '@/components/Navbar'

// 面包屑导航组件
const Breadcrumb = () => {
  return (
    <nav className="flex items-center space-x-2 text-sm text-gray-400 mb-8">
      <Link href="/" className="hover:text-orange-400 transition-colors">
        首页
      </Link>
      <span>/</span>
      <span className="text-white">关于我们</span>
    </nav>
  )
}

// 定义类型
interface Milestone {
  year: string;
  event: string;
}

interface Category {
  id: number;
  name: string;
  parentId: number;
  description?: string;
}

interface PageContent {
  pageContent?: string;
  backgroundImage?: string | null;
  categoryName?: string;
  description?: string;
}

export default function About() {
  const [activeTab, setActiveTab] = useState('')
  const [milestones, setMilestones] = useState<Milestone[]>([])
  const [companyContent, setCompanyContent] = useState<string>('')
  const [cultureContent, setCultureContent] = useState<string>('')
  const [pageCategories, setPageCategories] = useState<Category[]>([])
  const [pageContents, setPageContents] = useState<Record<number, PageContent>>({})
  const [loading, setLoading] = useState<boolean>(false)
  const [companyLoading, setCompanyLoading] = useState<boolean>(false)
  const [cultureLoading, setCultureLoading] = useState<boolean>(false)
  const [categoriesLoading, setCategoriesLoading] = useState<boolean>(false)

  // 使用fetch API获取发展历程数据
  useEffect(() => {
    const fetchHistoryData = async () => {
      setLoading(true)
      try {
        const response = await fetch('/api/history')
        const result = await response.json()
        
        if (result && result.success) {
          setMilestones(result.data)
        } else {
          console.error('获取发展历程失败:', result)
          // 如果API失败，使用默认数据
          setMilestones([
            { year: '2018', event: '公司成立，专注超黑材料研发' },
            { year: '2019', event: '太阳黑洞1.0发布，吸收率突破99%' },
            { year: '2020', event: '获得多项技术专利，进入航空航天领域' },
            { year: '2021', event: '太阳黑洞3.0发布，积分球反射率降至2%' },
            { year: '2022', event: '拓展汽车摄像头市场，与多家车企合作' },
            { year: '2023', event: '太阳黑洞5.0发布，吸收率达到99.8%' },
            { year: '2024', event: '太阳黑洞6.0发布，重新定义行业标准' }
          ])
        }
      } catch (error) {
        console.error('获取发展历程失败:', error)
        // 如果API失败，使用默认数据
        setMilestones([
          { year: '2018', event: '公司成立，专注超黑材料研发' },
          { year: '2019', event: '太阳黑洞1.0发布，吸收率突破99%' },
          { year: '2020', event: '获得多项技术专利，进入航空航天领域' },
          { year: '2021', event: '太阳黑洞3.0发布，积分球反射率降至2%' },
          { year: '2022', event: '拓展汽车摄像头市场，与多家车企合作' },
          { year: '2023', event: '太阳黑洞5.0发布，吸收率达到99.8%' },
          { year: '2024', event: '太阳黑洞6.0发布，重新定义行业标准' }
        ])
      } finally {
        setLoading(false)
      }
    }
    
    fetchHistoryData()
  }, [])

  // 使用fetch API获取公司介绍内容
  useEffect(() => {
    const fetchCompanyData = async () => {
      setCompanyLoading(true)
      try {
        const response = await fetch('/api/categories/12')
        const result = await response.json()
        
        if (result && result.success && result.data.pageContent) {
          setCompanyContent(result.data.pageContent)
        } else {
          console.error('获取公司介绍内容失败:', result)
          // 如果API失败，使用默认内容
          setCompanyContent(`
            <p>画玺科技成立于2018年，是一家专注于超黑材料研发、生产和应用的高新技术企业。公司总部位于中国深圳，在北京、上海、成都等地设有分支机构和研发中心。作为超黑材料领域的领军企业，画玺科技拥有完全自主知识产权的核心技术，其研发的超黑材料吸收率高达99.8%以上，在光学性能、稳定性和应用广度方面均达到国际领先水平。</p>
            <p>公司自成立以来，始终秉承"科技创新、质量至上"的经营理念，汇聚了一批来自材料科学、光学工程、应用物理等领域的顶尖人才。研发团队占比超过40%，其中博士学历占比30%，硕士学历占比50%。公司与清华大学、中科院、北京理工大学等知名科研院所建立了长期稳定的产学研合作关系，在超黑材料的基础研究和应用开发方面取得了突破性进展。</p>
            <p>画玺科技拥有现代化的生产基地和先进的生产设备，建立了完善的质量管理体系，通过了ISO9001质量管理体系认证、ISO14001环境管理体系认证和ISO45001职业健康安全管理体系认证。公司年产能达到100吨超黑材料，产品广泛应用于航空航天、国防军工、精密仪器、汽车制造、消费电子、建筑装饰等众多领域。</p>
            <p>在技术创新方面，画玺科技已获得发明专利32项，实用新型专利68项，软件著作权15项，参与制定行业标准8项。公司先后承担了国家科技重大专项、国家重点研发计划、工信部产业基础再造工程等多个国家级科研项目，在超黑材料领域的技术水平和产业化能力处于国内领先地位。</p>
            <p>面向未来，画玺科技将继续深耕超黑材料领域，加大研发投入，拓展应用场景，为客户提供更加优质的产品和服务。公司致力于成为全球领先的超黑材料解决方案提供商，推动超黑材料技术的产业化应用，为人类科技进步和社会发展贡献力量。</p>
          `)
        }
      } catch (error) {
        console.error('获取公司介绍内容失败:', error)
        // 如果API失败，使用默认内容
        setCompanyContent(`
          <p>画玺科技成立于2018年，是一家专注于超黑材料研发、生产和应用的高新技术企业。公司总部位于中国深圳，在北京、上海、成都等地设有分支机构和研发中心。作为超黑材料领域的领军企业，画玺科技拥有完全自主知识产权的核心技术，其研发的超黑材料吸收率高达99.8%以上，在光学性能、稳定性和应用广度方面均达到国际领先水平。</p>
          <p>公司自成立以来，始终秉承"科技创新、质量至上"的经营理念，汇聚了一批来自材料科学、光学工程、应用物理等领域的顶尖人才。研发团队占比超过40%，其中博士学历占比30%，硕士学历占比50%。公司与清华大学、中科院、北京理工大学等知名科研院所建立了长期稳定的产学研合作关系，在超黑材料的基础研究和应用开发方面取得了突破性进展。</p>
          <p>画玺科技拥有现代化的生产基地和先进的生产设备，建立了完善的质量管理体系，通过了ISO9001质量管理体系认证、ISO14001环境管理体系认证和ISO45001职业健康安全管理体系认证。公司年产能达到100吨超黑材料，产品广泛应用于航空航天、国防军工、精密仪器、汽车制造、消费电子、建筑装饰等众多领域。</p>
          <p>在技术创新方面，画玺科技已获得发明专利32项，实用新型专利68项，软件著作权15项，参与制定行业标准8项。公司先后承担了国家科技重大专项、国家重点研发计划、工信部产业基础再造工程等多个国家级科研项目，在超黑材料领域的技术水平和产业化能力处于国内领先地位。</p>
          <p>面向未来，画玺科技将继续深耕超黑材料领域，加大研发投入，拓展应用场景，为客户提供更加优质的产品和服务。公司致力于成为全球领先的超黑材料解决方案提供商，推动超黑材料技术的产业化应用，为人类科技进步和社会发展贡献力量。</p>
        `)
      } finally {
        setCompanyLoading(false)
      }
    }
    
    fetchCompanyData()
  }, [])

  // 使用fetch API获取企业文化内容
  useEffect(() => {
    const fetchCultureData = async () => {
      setCultureLoading(true)
      try {
        const response = await fetch('/api/categories/14')
        const result = await response.json()
        
        if (result && result.success && result.data.pageContent) {
          setCultureContent(result.data.pageContent)
        } else {
          console.error('获取企业文化内容失败:', result)
          // 如果API失败，使用默认内容
          setCultureContent(`
            <div class="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div class="bg-gray-900/30 backdrop-blur-lg rounded-xl p-8 border border-white/10 hover:border-orange-500/30 transition-all duration-300 group hover:scale-105">
                <div class="text-4xl mb-4">🚀</div>
                <h3 class="text-xl font-bold text-orange-400 mb-4">技术领先</h3>
                <p class="text-gray-300 leading-relaxed">持续投入研发，保持技术领先优势，推动行业技术边界</p>
              </div>
              <div class="bg-gray-900/30 backdrop-blur-lg rounded-xl p-8 border border-white/10 hover:border-orange-500/30 transition-all duration-300 group hover:scale-105">
                <div class="text-4xl mb-4">⭐</div>
                <h3 class="text-xl font-bold text-orange-400 mb-4">品质至上</h3>
                <p class="text-gray-300 leading-relaxed">严格的质量控制体系，确保每一个产品都达到最高标准</p>
              </div>
              <div class="bg-gray-900/30 backdrop-blur-lg rounded-xl p-8 border border-white/10 hover:border-orange-500/30 transition-all duration-300 group hover:scale-105">
                <div class="text-4xl mb-4">🎯</div>
                <h3 class="text-xl font-bold text-orange-400 mb-4">客户导向</h3>
                <p class="text-gray-300 leading-relaxed">深入了解客户需求，提供定制化的光学解决方案</p>
              </div>
              <div class="bg-gray-900/30 backdrop-blur-lg rounded-xl p-8 border border-white/10 hover:border-orange-500/30 transition-all duration-300 group hover:scale-105">
                <div class="text-4xl mb-4">💡</div>
                <h3 class="text-xl font-bold text-orange-400 mb-4">持续创新</h3>
                <p class="text-gray-300 leading-relaxed">不断探索新的可能性，创造更优秀的产品和服务</p>
              </div>
            </div>
          `)
        }
      } catch (error) {
        console.error('获取企业文化内容失败:', error)
        // 如果API失败，使用默认内容
        setCultureContent(`
          <div class="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div class="bg-gray-900/30 backdrop-blur-lg rounded-xl p-8 border border-white/10 hover:border-orange-500/30 transition-all duration-300 group hover:scale-105">
              <div class="text-4xl mb-4">🚀</div>
              <h3 class="text-xl font-bold text-orange-400 mb-4">技术领先</h3>
              <p class="text-gray-300 leading-relaxed">持续投入研发，保持技术领先优势，推动行业技术边界</p>
            </div>
            <div class="bg-gray-900/30 backdrop-blur-lg rounded-xl p-8 border border-white/10 hover:border-orange-500/30 transition-all duration-300 group hover:scale-105">
              <div class="text-4xl mb-4">⭐</div>
              <h3 class="text-xl font-bold text-orange-400 mb-4">品质至上</h3>
              <p class="text-gray-300 leading-relaxed">严格的质量控制体系，确保每一个产品都达到最高标准</p>
            </div>
            <div class="bg-gray-900/30 backdrop-blur-lg rounded-xl p-8 border border-white/10 hover:border-orange-500/30 transition-all duration-300 group hover:scale-105">
              <div class="text-4xl mb-4">🎯</div>
              <h3 class="text-xl font-bold text-orange-400 mb-4">客户导向</h3>
              <p class="text-gray-300 leading-relaxed">深入了解客户需求，提供定制化的光学解决方案</p>
            </div>
            <div class="bg-gray-900/30 backdrop-blur-lg rounded-xl p-8 border border-white/10 hover:border-orange-500/30 transition-all duration-300 group hover:scale-105">
              <div class="text-4xl mb-4">💡</div>
              <h3 class="text-xl font-bold text-orange-400 mb-4">持续创新</h3>
              <p class="text-gray-300 leading-relaxed">不断探索新的可能性，创造更优秀的产品和服务</p>
            </div>
          </div>
        `)
      } finally {
        setCultureLoading(false)
      }
    }
    
    fetchCultureData()
  }, [])

  // 使用fetch API获取关于我们栏目（ID:11）的子栏目
  useEffect(() => {
    const fetchAboutCategoryData = async () => {
      setCategoriesLoading(true)
      try {
        const response = await fetch('/api/categories/11')
        const result = await response.json()
        
        if (result && result.success && result.data.children) {
          const children = result.data.children
          
          // 为每个子栏目获取详细信息
          const categoryPromises = children.map(async (category: any) => {
            const detailResponse = await fetch(`/api/categories/${category.id}`)
            const detailResult = await detailResponse.json()
            
            if (detailResult.success) {
              return {
                ...category,
                pageContent: detailResult.data.pageContent || '',
                description: detailResult.data.description || ''
              }
            }
            return category
          })
          
          Promise.all(categoryPromises).then(detailedCategories => {
            setPageCategories(detailedCategories)
          }).catch(error => {
            console.error('获取子栏目详情失败:', error)
            setPageCategories([])
          })
        } else {
          console.error('获取关于我们栏目失败:', result)
          setPageCategories([])
        }
      } catch (error) {
        console.error('获取关于我们栏目失败:', error)
        setPageCategories([])
      } finally {
        setCategoriesLoading(false)
      }
    }
    
    fetchAboutCategoryData()
  }, [])

  // 处理URL哈希值变化
  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash.replace('#', '')
      if (hash) {
        // 处理动态栏目标签（格式：category-{id}）
        if (hash.startsWith('category-')) {
          setActiveTab(hash)
        }
        
        // 延迟滚动到对应元素
        setTimeout(() => {
          const element = document.getElementById(hash)
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'start' })
          }
        }, 300)
      }
    }

    // 初始检查
    handleHashChange()
    
    // 监听哈希变化
    window.addEventListener('hashchange', handleHashChange)
    return () => window.removeEventListener('hashchange', handleHashChange)
  }, [])

  // 监听标签页变化，更新URL哈希
  useEffect(() => {
    if (activeTab && window.location.hash !== `#${activeTab}`) {
      window.history.replaceState(null, '', `#${activeTab}`)
    }
  }, [activeTab])

  // 组件挂载时获取发展历程数据和公司介绍内容
  useEffect(() => {
    // 所有数据现在通过缓存Hook自动获取
    // 不再需要手动调用fetch函数
  }, [])

  const companyInfo = {
    name: '画玺科技',
    founded: '2018年',
    headquarters: '中国深圳',
    employees: '200+',
    mission: '重新定义黑色的极限标准，为全球客户提供极致的光学解决方案',
    vision: '成为全球超黑材料技术的领导者，推动光学科技的边界'
  }

  // 默认数据（仅在API失败时使用）
  const defaultMilestones = [
    { year: '2018', event: '公司成立，专注超黑材料研发' },
    { year: '2019', event: '太阳黑洞1.0发布，吸收率突破99%' },
    { year: '2020', event: '获得多项技术专利，进入航空航天领域' },
    { year: '2021', event: '太阳黑洞3.0发布，积分球反射率降至2%' },
    { year: '2022', event: '拓展汽车摄像头市场，与多家车企合作' },
    { year: '2023', event: '太阳黑洞5.0发布，吸收率达到99.8%' },
    { year: '2024', event: '太阳黑洞6.0发布，重新定义行业标准' }
  ]

  const values = [
    {
      title: '技术领先',
      description: '持续投入研发，保持技术领先优势，推动行业技术边界',
      icon: '🚀'
    },
    {
      title: '品质至上',
      description: '严格的质量控制体系，确保每一个产品都达到最高标准',
      icon: '⭐'
    },
    {
      title: '客户导向',
      description: '深入了解客户需求，提供定制化的光学解决方案',
      icon: '🎯'
    },
    {
      title: '持续创新',
      description: '不断探索新的可能性，创造更优秀的产品和服务',
      icon: '💡'
    }
  ]

  return (
    <main className="min-h-screen bg-black relative">
      {/* 动态背景装饰 */}
      <div className="absolute inset-0 opacity-10 z-10">
        <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-gradient-to-r from-blue-500/20 to-purple-600/20 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute top-1/2 right-1/4 w-48 h-48 bg-gradient-to-r from-orange-500/20 to-red-600/20 rounded-full blur-3xl animate-pulse delay-1000"></div>
        <div className="absolute bottom-1/4 left-1/3 w-56 h-56 bg-gradient-to-r from-cyan-500/20 to-blue-600/20 rounded-full blur-3xl animate-pulse delay-2000"></div>
      </div>

      {/* 导航栏 */}
      <Navbar />

      {/* 页面头部 - 带背景图版 (与联系页面相同的设计) */}
      <section className="relative py-32 overflow-hidden">
        {/* 背景图片 - 使用栏目自己的背景图，如果没有就不展示 */}
        {(() => {
          const currentCategory = pageCategories.find((cat: Category) => activeTab === `category-${cat.id}`);
          const categoryData = currentCategory ? pageContents[currentCategory.id] : null;
          const backgroundImage = categoryData?.backgroundImage;
          
          if (backgroundImage) {
            return (
              <div 
                className="absolute inset-0 z-0"
                style={{
                  backgroundImage: `url(${backgroundImage})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  backgroundRepeat: 'no-repeat',
                  filter: 'brightness(0.5) contrast(1.1)'
                }}
              >
                {/* 渐变遮罩层 */}
                <div className="absolute inset-0 bg-gradient-to-br from-black/50 via-black/30 to-transparent"></div>
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent"></div>
              </div>
            );
          }
          return null;
        })()}
        
        {/* 动态背景装饰 */}
        <div className="absolute inset-0 opacity-15 z-10">
          <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-gradient-to-r from-blue-500/20 to-purple-600/20 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute top-1/2 right-1/4 w-48 h-48 bg-gradient-to-r from-orange-500/20 to-red-600/20 rounded-full blur-3xl animate-pulse delay-1000"></div>
          <div className="absolute bottom-1/4 left-1/3 w-56 h-56 bg-gradient-to-r from-cyan-500/20 to-blue-600/20 rounded-full blur-3xl animate-pulse delay-2000"></div>
        </div>
        
        <div className="relative max-w-6xl mx-auto px-6 text-center z-20 mt-16">
          <motion.h1 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-3xl md:text-5xl font-bold mb-6 bg-gradient-to-r from-white via-blue-100 to-purple-200 bg-clip-text text-transparent leading-tight drop-shadow-xl"
          >
            {(() => {
              const currentCategory = pageCategories.find((cat: Category) => activeTab === `category-${cat.id}`);
              const categoryData = currentCategory ? pageContents[currentCategory.id] : null;
              return categoryData?.categoryName || currentCategory?.name || '关于我们';
            })()}
          </motion.h1>
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed drop-shadow-lg"
          >
            {(() => {
              const currentCategory = pageCategories.find((cat: Category) => activeTab === `category-${cat.id}`);
              const categoryData = currentCategory ? pageContents[currentCategory.id] : null;
              return categoryData?.description || currentCategory?.description || '了解画玺科技的发展历程、企业文化和核心价值';
            })()}
          </motion.p>
        </div>
        
        {/* 底部渐变过渡 */}
        <div className="absolute bottom-0 left-0 right-0 h-20 bg-gradient-to-t from-black to-transparent z-20"></div>
      </section>

      {/* 装饰性分割线 */}
      <div className="relative z-30">
        <div className="max-w-6xl mx-auto px-6">
          <div className="h-px bg-gradient-to-r from-transparent via-gray-600 to-transparent"></div>
          <div className="h-px bg-gradient-to-r from-transparent via-gray-700/50 to-transparent mt-px"></div>
        </div>
      </div>

      {/* 主要内容区域 */}
      <div className="pt-16 pb-16 relative z-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">


          {/* 标签切换 */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="flex justify-center mb-12"
          >
            {categoriesLoading ? (
              <div className="bg-gray-900/40 backdrop-blur-md rounded-2xl p-4 flex space-x-2 border border-gray-700/30">
                <div className="animate-pulse bg-gray-700/50 rounded-xl px-6 py-3 w-24"></div>
                <div className="animate-pulse bg-gray-700/50 rounded-xl px-6 py-3 w-24"></div>
                <div className="animate-pulse bg-gray-700/50 rounded-xl px-6 py-3 w-24"></div>
              </div>
            ) : (
              <div className="bg-gray-900/40 backdrop-blur-md rounded-2xl p-2 flex space-x-2 border border-gray-700/30">
                {/* 动态生成单页栏目标签（只显示关于我们栏目的子栏目） */}
                {pageCategories.map((category) => (
                  <button
                    key={category.id}
                    onClick={() => setActiveTab(`category-${category.id}`)}
                    className={`px-6 py-3 rounded-xl font-medium transition-all duration-300 ${
                      activeTab === `category-${category.id}`
                        ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg shadow-blue-500/25'
                        : 'text-gray-300 hover:text-white hover:bg-gray-800/50'
                    }`}
                  >
                    {category.name}
                  </button>
                ))}
              </div>
            )}
          </motion.div>

          {/* 内容区域 */}
          <div className="bg-gray-900/30 backdrop-blur-md rounded-3xl p-8 border border-gray-700/30 shadow-2xl shadow-black/20">
            <AnimatePresence mode="wait">
              {/* 动态渲染单页栏目内容 */}
              {pageCategories.map((category) => (
                activeTab === `category-${category.id}` && (
                  <motion.div
                    key={`category-${category.id}`}
                    id={`category-${category.id}`}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    className="space-y-12"
                  >
                    {category.id === 13 ? (
                      <div className="space-y-12">
                        {/* 发展历程标题和描述 */}
                        <div className="text-center">
                          <h3 className="text-3xl md:text-5xl font-bold mb-6 bg-gradient-to-r from-white via-blue-100 to-purple-200 bg-clip-text text-transparent leading-tight drop-shadow-xl">
                            {category.name}
                          </h3>
                          <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed drop-shadow-lg">
                            {category.description || '了解画玺科技的发展历程、企业文化和核心价值'}
                          </p>
                        </div>
                        
                        {/* 时间线 */}
                        <div className="relative">
                          <div className="absolute left-1/2 transform -translate-x-1/2 w-1 h-full bg-gradient-to-b from-orange-500 to-red-600 rounded-full"></div>
                          {milestones.map((milestone, index) => (
                            <motion.div
                              key={index}
                              initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
                              animate={{ opacity: 1, x: 0 }}
                              transition={{ delay: index * 0.1 }}
                              className={`flex items-center mb-12 ${index % 2 === 0 ? 'flex-row' : 'flex-row-reverse'}`}
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.95 }}
                            >
                              <div className={`w-1/2 ${index % 2 === 0 ? 'pr-8 text-right' : 'pl-8 text-left'}`}>
                                <motion.div 
                                  className="bg-gray-900/50 backdrop-blur-lg rounded-xl p-6 border border-white/10 cursor-pointer group hover:border-orange-500/50 transition-all duration-300"
                                  whileHover={{ 
                                    boxShadow: '0 20px 40px rgba(249, 115, 22, 0.3)',
                                    borderColor: 'rgba(249, 115, 22, 0.8)'
                                  }}
                                >
                                  <motion.div 
                                    className="text-orange-400 font-bold text-lg mb-2 group-hover:text-orange-300 transition-colors duration-300"
                                    whileHover={{ scale: 1.1 }}
                                  >
                                    {milestone.year}
                                  </motion.div>
                                  <motion.div 
                                    className="text-gray-300 group-hover:text-white transition-colors duration-300"
                                    initial={{ opacity: 0.8 }}
                                    whileHover={{ opacity: 1 }}
                                  >
                                    {milestone.event}
                                  </motion.div>
                                </motion.div>
                              </div>
                              <motion.div 
                                className="absolute left-1/2 transform -translate-x-1/2 w-4 h-4 bg-orange-500 rounded-full border-4 border-black z-10"
                                whileHover={{ 
                                  scale: 1.5,
                                  backgroundColor: 'rgb(251, 146, 60)',
                                  boxShadow: '0 0 20px rgba(249, 115, 22, 0.8)'
                                }}
                                transition={{ type: 'spring', stiffness: 300 }}
                              />
                              <div className="w-1/2"></div>
                            </motion.div>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <div className="bg-gray-900/30 backdrop-blur-lg rounded-3xl p-8 border border-white/10">
                        <h3 className="text-3xl font-bold text-white mb-8 text-center">{category.name}</h3>
                        {categoriesLoading ? (
                          <div className="text-center py-8">
                            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                            <p className="mt-2 text-gray-300">加载内容中...</p>
                          </div>
                        ) : pageContents[category.id]?.pageContent ? (
                          <div 
                            className="space-y-6 text-gray-300 leading-relaxed"
                            dangerouslySetInnerHTML={{ __html: pageContents[category.id].pageContent || '' }}
                          />
                        ) : (
                          <div className="text-center py-8">
                            <p className="text-gray-400">暂无内容数据</p>
                          </div>
                        )}
                      </div>
                    )}
                  </motion.div>
                )
              ))}
            </AnimatePresence>
          </div>
        </div>
      </div>



      {/* 页脚 */}

        <Footer />
    </main>
  )
}